import {useEffect} from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {DialogContent, useTheme, Box, Typography} from '@mui/material';
import {grey} from '@mui/material/colors';
import {useLocation} from 'react-router-dom';
import styled from 'styled-components';

import ChooseConnectorLoginProcessModal from './ChooseConnectorLoginProcessModal/ChooseConnectorLoginProcessModal';
import {CONNECTORS_ON_CLICK_ACTIONS} from '../../../const';
import useContact from '../../../hooks/providers/useContact';
import useProjects from '../../../hooks/providers/useProjects';
import useWorkspaces from '../../../hooks/providers/useWorkspaces';
import CustomAlert from '../../alert/Alert';
import OpenContactFormButton from '../../contact/OpenContactFormButton/OpenContactFormButton';
import CustomDimmer from '../../dimmer/Dimmer';
import Button from '../../form/buttons/Button/Button';
import BaseModal from '../../modal/BaseModal';
import Spinner from '../../spinner/Spinner';
import LoginToConnector from './connector-login/LoginToConnector/LoginToConnector';
import ConnectorFoldersListForm from './ConnectorFoldersListForm/ConnectorFoldersListForm';
import Connectors from './Connectors/Connectors';
import ManualConnectorForm from './ManualConnectorForm';
import useLoading from '../../../hooks/providers/useLoading';

const ModalTitleConnectorLogo = styled.img`
  max-height: 25px;
`;

// eslint-disable-next-line complexity
const CreateDatalakeConnectionModal = () => {
  const {
    isSubmitting,
    connector,
    setConnector,
    checkConnectorConnection,
    setLoginToConnectorFormData,
    setIsSuccessfullyLoggedInToConnector,
    isSuccessfullyLoggedInToConnector,
    setSiren,
    createProjectModalOpen,
    setCreateProjectModalOpen,
    isConnectorsLoading,
    isRedirectingToConnectorAuthenticationServer,
    setFoldersCompanyIdsToConnect,
    setIsRedirectingToConnectorAuthenticationServer,
    createConnectionFormStep: creationStep,
    setCreateConnectionFormStep: setCreationStep
  } = useProjects();
  const {setShouldDisplayNonBrandedSpinner} = useLoading();
  const {openAskConnectorOpeningContactForm} = useContact();
  const {isDefaultWorkspace} = useWorkspaces();

  const parentTheme = useTheme();
  const location = useLocation();

  const open = createProjectModalOpen && isDefaultWorkspace;

  const onClose = () => {
    setCreateProjectModalOpen(false);
  };

  const resetFormState = () => {
    setLoginToConnectorFormData({});
    setFoldersCompanyIdsToConnect([]);
  };

  // This hook aims to reset loading state / close create project modal
  // When user hits back button from an external url (Connector OAuth page)
  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const handlePageShow = event => {
      if (event.persisted && isSafari) {
        setIsRedirectingToConnectorAuthenticationServer(false);
        setCreateProjectModalOpen(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    if (!open) {
      setConnector(null);
      setCreationStep(0);
    }

    resetFormState();
  }, [open]);

  useEffect(() => {
    // Once user has selected a connector, we get to the next form step
    if (connector) {
      setCreationStep(1);

      // When being redirected from an OAuth callback page with isProjectCreationModalOpen: true, we know that user has successfully
      // Logged-in into his connector OAuth account, so we do no call setIsSuccessfullyLoggedInToConnector(false)
      if (!location.state?.isProjectCreationModalOpen) {
        setIsSuccessfullyLoggedInToConnector(false);
      } else {
        setShouldDisplayNonBrandedSpinner(false);
      }
    }
  }, [connector]);

  useEffect(() => {
    if (connector && connector.open_on_click === CONNECTORS_ON_CLICK_ACTIONS.OPEN_CONTACT_FORM) {
      onClose();
      openAskConnectorOpeningContactForm(connector);
    }
  }, [connector]);

  // We reset errors when switching connector in form
  // because error is shared between all connectors
  useEffect(() => {
    setSiren('');
    resetFormState();
  }, [connector]);

  const getModalTitle = () => {
    if (creationStep === 0) {
      return 'Créer une connexion';
    }

    if (creationStep === 1 && connector.is_manual) {
      return 'Importer manuellement';
    }

    return <ModalTitleConnectorLogo alt="logo connecteur" src={connector.url_logo} />;
  };

  const resetConnectionForm = () => {
    setConnector(null);
    setCreationStep(0);
  };

  const shouldDisplayConnectors = creationStep === 0 && !isSubmitting;
  const shouldDisplayManualConnectorForm = creationStep === 1 && !isSuccessfullyLoggedInToConnector && connector.is_manual;
  const shouldDisplayLoginForm =
    creationStep === 1 && !isSuccessfullyLoggedInToConnector && !connector.is_manual && connector.open_on_click === CONNECTORS_ON_CLICK_ACTIONS.OPEN_CREATE_CONNECTION_FORM;
  const shouldDisplayFoldersListForm = creationStep === 1 && isSuccessfullyLoggedInToConnector;

  const title = getModalTitle();

  return (
    <BaseModal hasTitleDivider={creationStep === 1} onClose={onClose} open={open} title={title}>
      {isConnectorsLoading ? (
        <Spinner isLoading text="Chargement des connecteurs..." />
      ) : (
        <>
          <ChooseConnectorLoginProcessModal resetConnectionForm={resetConnectionForm} />
          <CustomDimmer isOpen={isRedirectingToConnectorAuthenticationServer} spinnerText={`Connexion à ${connector?.name}`} />
          <DialogContent>
            {creationStep === 1 && (
              <Box sx={{position: 'sticky', top: '-8px', background: 'white', zIndex: 2}}>
                <Button
                  sx={{
                    color: 'text.primary'
                  }}
                  startIcon={<ArrowBackIcon />}
                  onClick={resetConnectionForm}
                >
                  Retour
                </Button>
              </Box>
            )}

            {shouldDisplayConnectors ? <Connectors /> : null}

            <Box py={3}>
              {shouldDisplayManualConnectorForm ? <ManualConnectorForm /> : null}
              {shouldDisplayLoginForm && !location.state?.isProjectCreationModalOpen ? <LoginToConnector /> : null}
              {shouldDisplayFoldersListForm ? <ConnectorFoldersListForm /> : null}
            </Box>
          </DialogContent>

          <CustomAlert sx={{background: parentTheme.palette.primary.light, color: grey[700]}}>
            <Typography fontSize={14}>Vous souhaitez un nouveau connecteur ?&nbsp;</Typography>
            <OpenContactFormButton text="Contactez-nous !" />
          </CustomAlert>
        </>
      )}
    </BaseModal>
  );
};

export default CreateDatalakeConnectionModal;
